import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import { SANITIZE_ATTRS, formatPostDate } from "@/lib/blog";
import LeadForm, { decodeLead, type LeadConfig } from "./LeadForm";

type Props = {
  title: string;
  category?: string | null;
  publishedAt?: string | null;
  coverUrl?: string | null;
  content: string;
  preview?: boolean;
};

/** Shared article renderer used by the public page and the admin preview. */
const ArticleBody = ({ title, category, publishedAt, coverUrl, content, preview }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const html = useMemo(() => DOMPurify.sanitize(content, { ADD_ATTR: SANITIZE_ATTRS }), [content]);
  const [slots, setSlots] = useState<{ el: Element; cfg: LeadConfig }[]>([]);

  useLayoutEffect(() => {
    const nodes = Array.from(ref.current?.querySelectorAll("[data-leadform]") ?? []);
    setSlots(nodes.map((el) => ({ el, cfg: decodeLead(el.getAttribute("data-leadform") ?? "") })).filter((s): s is { el: Element; cfg: LeadConfig } => !!s.cfg));
  }, [html]);

  return (
    <>
      {category && <span className="text-primary font-semibold tracking-widest text-sm uppercase">{category}</span>}
      <h1 className="font-heading text-3xl md:text-5xl font-bold mt-3 mb-4 leading-tight">{title || "Sem título"}</h1>
      <p className="text-muted-foreground mb-8">{formatPostDate(publishedAt ?? new Date().toISOString())}</p>
      {coverUrl && <img src={coverUrl} alt={title} className="w-full rounded-2xl object-cover aspect-video mb-10" fetchPriority="high" />}
      <div
        ref={ref}
        className="prose prose-neutral prose-lg max-w-none prose-headings:font-heading prose-a:text-primary prose-blockquote:border-primary"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {slots.map((s, i) => createPortal(<LeadForm config={s.cfg} article={title} position={i + 1} preview={preview} />, s.el, `lead-${i}`))}
    </>
  );
};

export default ArticleBody;
